import { useEffect, useMemo } from "react";
import { apiFetch } from "../../../../toolympus/api/core";
import { useUserSettings } from "../../../../toolympus/components/Settings/UserSettingsContext";
import { useLoadedData } from "../../../../toolympus/hooks/useLoadedData";
import { Project } from "../../types";
import { EditingSettings } from "../../../Settings/useAppSettings";
import { useDictionaries } from "../../../../toolympus/hooks/useDictionaries";
import moment, { Moment } from "moment";
import { addWorkDays, subtractWorkDays } from "./datesHelpers";

export type ProjectHighlightColor = "red" | "orange" | "dark-gray" | "none";

export interface AugmentedEditorProject extends Project {
  complexityFactor?: number;
  /** Calculated duration in work days (Math.ceil(size / speed * complexity)) */
  durationWorkDays?: number;
  /** Start date of the task bar (ISO string YYYY-MM-DD) */
  barStartDate?: string;
  /** End date of the task bar (ISO string YYYY-MM-DD) */
  barEndDate?: string;
  /** Effective target deadline taking buffer into account */
  effectiveDeadline?: string;
  /** Whether the start date was explicitly set by the editor */
  isManualStart: boolean;
  /** Row highlight color based on project state and conflicts */
  highlightColor: ProjectHighlightColor;
  /** Flags describing exact status rules */
  statusFlags: {
    missingSize: boolean;
    overdueDeadline: boolean;
    overdueBuffer: boolean;
    hasOverlap: boolean;
    isStartMovedToToday?: boolean;
  };
}

export const useEditingSettings = () => {
  const userSettings = useUserSettings();

  useEffect(() => {
    userSettings.ensureLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settings = (userSettings.global || {}) as EditingSettings & Record<string, any>;

  return {
    settings,
  };
};

export const ProjectEditingComplexityDictionaryCode = "ProjectEditingComplexity";

export const useEditorPlan = () => {
  const applicableProjects = useLoadedData<Project[]>(`/api/project/editing-plan`, []);
  const { settings } = useEditingSettings();
  const dictionaries = useDictionaries();

  const complexityDictionary = dictionaries[ProjectEditingComplexityDictionaryCode];
  const complexityToComplexityFactor = useMemo(() => {
    const mapper = (complexityDictionary?.records || []).reduce<Record<string, number>>((r, v) => {
      r[v.code] = (v.extra?.complexity || 100) * 0.01;
      return r;
    }, {});

    return (v: string | undefined | null) => mapper[v || ""] || 1.0;
  }, [complexityDictionary?.records]);

  const updateProject = async (changesAndId: Partial<Project>) => {
    const changes: any = { ...changesAndId };
    delete changes._id;
    await apiFetch<Project>(`/api/project/project/${changesAndId._id}`, "put", changes);
    applicableProjects.reload();
    return {};
  };

  const projects = useMemo<AugmentedEditorProject[]>(() => {
    const rawProjects = applicableProjects.data || [];
    const speed = settings.editing_speed_pages_per_day || 1;
    const bufferDays = settings.editing_buffer_days || 0;
    const today = moment().startOf("day");

    // Step 1: Compute basic bar dates and durations
    const augmentedWithoutOverlap: AugmentedEditorProject[] = rawProjects.map((project) => {
      const hasSize = typeof project.editing_size === "number" && project.editing_size > 0;
      const complexityFactor = complexityToComplexityFactor(project.editing_complexity);
      const isManualStart = Boolean(project.editing_start_date);

      if (!hasSize) {
        return {
          ...project,
          isManualStart,
          highlightColor: "dark-gray" as ProjectHighlightColor,
          statusFlags: {
            missingSize: true,
            overdueDeadline: false,
            overdueBuffer: false,
            hasOverlap: false,
          },
        };
      }

      // Workday duration = Math.ceil((size / speed) * complexityFactor)
      const durationWorkDays = Math.max(1, Math.ceil((project.editing_size! / speed) * complexityFactor));

      let barStart: Moment;
      let barEnd: Moment;
      let isStartMovedToToday = false;
      const deadline = moment(project.deadline).startOf("day");
      // Deadline minus buffer workdays
      const deadlineWithBuffer = bufferDays > 0 ? subtractWorkDays(deadline, bufferDays + 1) : deadline.clone();

      if (isManualStart) {
        barStart = moment(project.editing_start_date).startOf("day");
        barEnd = addWorkDays(barStart, durationWorkDays);
      } else {
        const tentativeStart = subtractWorkDays(deadlineWithBuffer, durationWorkDays);

        // Если дата старта в будущем или сегодня (>= today) — откладываем от конца
        if (tentativeStart.isSameOrAfter(today, "day")) {
          barStart = tentativeStart;
          barEnd = deadlineWithBuffer.clone();
        } else {
          // Иначе старт начинается от сегодня, а конец рассчитывается вперед
          barStart = today.clone();
          barEnd = addWorkDays(barStart, durationWorkDays);
          isStartMovedToToday = true;
        }
      }

      const overdueDeadline = barEnd.isAfter(deadline, "day");
      const overdueBuffer = !overdueDeadline && barEnd.isAfter(deadlineWithBuffer, "day");

      return {
        ...project,
        complexityFactor,
        durationWorkDays,
        barStartDate: barStart.format("YYYY-MM-DD"),
        barEndDate: barEnd.format("YYYY-MM-DD"),
        effectiveDeadline: deadlineWithBuffer.format("YYYY-MM-DD"),
        isManualStart,
        highlightColor: "none" as ProjectHighlightColor,
        statusFlags: {
          missingSize: false,
          overdueDeadline,
          overdueBuffer,
          hasOverlap: false,
          isStartMovedToToday,
        },
      };
    });

    // Step 2: Detect overlaps across valid bars
    return augmentedWithoutOverlap.map((project, idx, arr) => {
      if (project.statusFlags.missingSize || !project.barStartDate || !project.barEndDate) {
        return project;
      }

      const currentStart = moment(project.barStartDate);
      const currentEnd = moment(project.barEndDate);

      const hasOverlap = arr.some((other, otherIdx) => {
        if (idx === otherIdx || other.statusFlags.missingSize || !other.barStartDate || !other.barEndDate) {
          return false;
        }
        const otherStart = moment(other.barStartDate);
        const otherEnd = moment(other.barEndDate);

        // Date ranges overlap if StartA <= EndB and EndA >= StartB
        return currentStart.isSameOrBefore(otherEnd, "day") && currentEnd.isSameOrAfter(otherStart, "day");
      });

      const statusFlags = {
        ...project.statusFlags,
        hasOverlap,
      };

      // Step 3: Determine final highlight color priority (Red > Orange > None)
      let highlightColor: ProjectHighlightColor = "none";
      if (statusFlags.overdueDeadline) {
        highlightColor = "red";
      } else if (statusFlags.overdueBuffer || statusFlags.hasOverlap) {
        highlightColor = "orange";
      }

      return {
        ...project,
        statusFlags,
        highlightColor,
      };
    });
  }, [applicableProjects.data, settings, complexityToComplexityFactor]);

  return {
    applicableProjects,
    projects,
    updateProject,
    settings,
  };
};

export type EditorPlanData = ReturnType<typeof useEditorPlan>;